import {
  defineScenario,
  act,
  beat,
  gp,
  infra,
  at,
  PACING,
  establishingWide,
  dronePushIn,
  orbit180,
  craneReveal,
  majesticPullback,
  hudEvent,
  hudTeam,
  hudRegion,
  notification,
  productScreen,
  panel,
  updatePanel,
  removePanel,
  spawnVehicle,
  convergence,
  matchBeam,
  gaugeCluster,
  dimStart,
  dimEnd,
  hold,
} from '../authoring';
import type { ScenarioStep } from '../../core/types';

// ──────────────────────────────────────────────────────────────────────────
// SCENARIO — THE COMBINED SOLUTION (follows the shooting script, Scene 1–7)
//
// This is the combined-video script played live in the twin. The seven acts
// ARE the seven scenes, in order and at the script's altitude arc — city-wide
// and reactive → the operating layer assembles → down through Capital, ORP,
// Control Room and Opus to one street → pull back out.
//
// It is NOT shown as marketing. No on-screen slogans, no value-slate
// percentages. Every beat is an operational event with a named entity and a
// real number — the register of burst-main (sensor P-2847, valve V-4420,
// T-104 ETA). The concrete thread is one mains scheme, MRP-4471 (Elm Avenue,
// 180m of 4" CI laid 1962, £820K), followed from the portfolio down to the dig.
//
//   Scene 1  THE PROBLEM                0:00–0:30  city-wide, reactive, siloed
//   Scene 2  ONE OPERATING LAYER        0:30–1:00  four systems connect to hub
//   Scene 3  CAPITAL DELIVERY           1:00–1:30  portfolio RAG, gate, AI risk
//   Scene 4  OPTIMAL RESOURCE PLANNING  1:30–2:05  demand → bundle → match crew
//   Scene 5  THE CONTROL ROOM           2:05–2:40  scheme cards, jeopardy alert
//   Scene 6  STREET / OPUS / CUSTOMER   2:40–3:20  one permit, one dig, reinstate
//   Scene 7  COMBINED VALUE & CLOSE     3:20–3:45  pull back, scheme closed
//
// BESPOKE EFFECTS built for this scenario (in EffectsSystem):
//   • convergence   — source job-pins stream tokens that FUSE into one hub
//                     (Scene 2 systems-assemble, Scene 4 demand-in, Scene 6 dig)
//   • match-beam    — ORP radar sweep that LOCKS onto the best-fit crew (Sc 4)
//   • gauge-cluster — the four ORP match dials, drawn in-world as 270° gauge
//                     arcs by the winning crew (Scene 4)
// Module colour key: Capital #1e40af · ORP #2dd4bf · Control #0ea5e9 · Opus #f59e0b
// productScreen beats left as TODO comments — add screenshots to public/screens.
// ──────────────────────────────────────────────────────────────────────────

// ─── Anchors ─────────────────────────────────────────────────────────────
const HUB = infra('control-centre', gp(-2, -4));        // the operating-layer hub

// The concrete scheme thread — MRP-4471, Elm Avenue mains renewal
const SCHEME = infra('sub-south', gp(11, 22));          // its node in the portfolio
const ELM = infra('mains-renewal-st', gp(9, 23));       // the street itself
const DIG_HOLE: [number, number] = gp(9, 23);
const DIG_PIPE: [number, number][] = [gp(7, 23), gp(8, 23), gp(9, 23), gp(10, 23), gp(11, 23)];

// The governance exception caught in Scene 3 (a different scheme, to show
// Capital Delivery doing its job)
const EXCEPTION = infra('depot-east', gp(14, -16));     // KTM-2209, in exception

// ORP candidate crews (Scene 4). NV-12 wins; NV-08 / NV-33 are swept past.
const GANG_NV12 = infra('depot-west', gp(-20, 10));

// The 38 residents on Elm Avenue (Scene 6 — notified once)
const ELM_RESIDENTS: [number, number][] = [
  gp(7, 22), gp(8, 22), gp(9, 22), gp(10, 22), gp(11, 22),
  gp(7, 24), gp(8, 24), gp(9, 24), gp(10, 24), gp(11, 24),
];

// Full-portfolio constellation used in Scenes 1/3/7 (status set per scene)
type Rag = 'red' | 'amber' | 'green';
const portfolio = (states: Rag[]): Array<{ coordinates: [number, number]; status: Rag }> => {
  const coords: [number, number][] = [
    gp(11, 22), gp(14, -16), gp(-25, -12), gp(22, 22),
    gp(26, -24), gp(-22, -22), gp(-26, 6), gp(-18, 28),
  ];
  return coords.map((coordinates, i) => ({ coordinates, status: states[i] }));
};

export const combinedSolutionScenario = defineScenario(
  {
    id: 'combined-solution',
    title: 'The Combined Solution — Invest, Plan, Deliver, Street',
    description: 'The full operating layer played end to end: a reactive, siloed estate connects into one source of truth, and a single mains scheme is governed, planned, delivered and reinstated across Capital Delivery, ORP, the Control Room and Opus.',
    hudLabel: 'COMBINED SOLUTION',
    telemetry: 'combined-solution',
    activeInfraIds: [
      'control-centre', 'sub-south', 'mains-renewal-st', 'depot-east',
      'depot-west', 'depot-south', 'pump-beta', 'gas-reg',
    ],
  },
  [
    // ═══════════════════════════════════════════════════════════════════════
    // SCENE 1 — THE PROBLEM  (0:00–0:30)
    // City-wide, reactive, run across disconnected systems. Scattered RAG with
    // NO links to a hub = no single view. Failures surface after the fact.
    // ═══════════════════════════════════════════════════════════════════════
    act('Scene 1 · The Problem', [
      dimStart,
      { type: 'color-grade', duration: 0, params: { tint: '#1e293b', intensity: 0.09, vignette: 0.24 } } as ScenarioStep,

      beat(PACING.ESTABLISH, [
        establishingWide(HUB, { bearing: -35, pitch: 46, zoom: 13 }),
        // Scattered work, all over the estate, unlinked — the siloed picture
        { type: 'multi-site-rag', duration: 0, params: {
          id: 'siloed',
          sites: portfolio(['amber', 'red', 'amber', 'red', 'amber', 'green', 'amber', 'red']),
        }},
        hudEvent('alert', 'RUN ACROSS DISCONNECTED SYSTEMS', 'Capital, planning, programme and street data sit in four separate tools. No single view of the work.', 'critical'),
        hudRegion([
          { name: 'Connections open', status: 'amber', properties: 412 },
          { name: 'Maintenance due', status: 'red', properties: 1290 },
          { name: 'MRP schemes live', status: 'amber', properties: 87 },
        ]),
      ]),

      // Reactive failures — discovered too late, each in its own silo
      beat(PACING.PUSH_IN, [
        dronePushIn(at(6, -2), { bearing: 15, zoom: 14.5, pitch: 55 }),
        hudEvent('alert', 'FPN ISSUED — £5,000', 'Permit PM-3310 overran on a major road. Fixed penalty raised — flagged only after it landed.', 'critical'),
        notification('NO SINGLE SOURCE OF TRUTH', 'Four systems, four versions of the truth. Clashes, overruns and missed SLAs found in hindsight.', 'critical', PACING.BEAT),
      ]),

      productScreen('Operations — Today', {
        component: 'combined-problem',
        device: 'desktop',
        subtitle: 'Four disconnected systems · no single source of truth',
        role: 'Duty Manager',
        url: 'app.skewb.io/ops/today',
      }),

      { type: 'clear-effect', duration: 0, params: { id: 'siloed' } } as ScenarioStep,
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // SCENE 2 — ONE OPERATING LAYER  (0:30–1:00)
    // The four systems connect onto one spine. The scattered work re-links to
    // the hub; four module feeds converge into it. Invest → Plan → Deliver → Street.
    // ═══════════════════════════════════════════════════════════════════════
    act('Scene 2 · One Operating Layer', [
      { type: 'color-grade', duration: 0, params: { tint: '#2dd4bf', intensity: 0.05, vignette: 0.14 } } as ScenarioStep,
      { type: 'model-status', duration: 0, params: { coordinates: gp(-2, -4), infraId: 'control-centre', color: '#2dd4bf', emissive: 0.7 } } as ScenarioStep,

      // The same sites — now LINKED to the hub (a single connected view)
      { type: 'multi-site-rag', duration: 0, params: {
        id: 'connected',
        sites: portfolio(['amber', 'red', 'amber', 'amber', 'amber', 'green', 'amber', 'amber']),
        linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
      }} as ScenarioStep,

      // Four module feeds converge onto the hub (the spine assembling)
      beat(PACING.PUSH_IN, [
        { type: 'camera', duration: PACING.PUSH_IN, params: { center: gp(-2, -4), infraId: 'control-centre', zoom: 14.5, pitch: 52, bearing: 5 } },
        convergence('layer-assemble',
          [at(22, 22), at(-20, 10), at(7, 25), at(9, 23)],   // Capital · ORP · Control · Opus
          HUB, PACING.PUSH_IN, { color: '#2dd4bf', speed: 0.55 }),
        hudEvent('layer', 'ONE OPERATING LAYER ONLINE', 'Capital Delivery, ORP, the Control Room and Opus connected to one source of truth — board to field.', 'success'),
        hudTeam([
          { name: 'Capital Delivery', status: 'on-track', destination: 'Invest — online' },
          { name: 'Resource Planning', status: 'on-track', destination: 'Plan — online' },
          { name: 'Control Room', status: 'on-track', destination: 'Deliver — online' },
          { name: 'Opus', status: 'on-track', destination: 'Street — online' },
        ]),
        notification('CONNECTED', 'The same work, now visible end to end — one place to govern it, plan it, deliver it and coordinate the street.', 'success', PACING.BEAT),
      ]),

      productScreen('One Operating Layer', {
        component: 'combined-layer',
        device: 'desktop',
        subtitle: 'Capital · ORP · Control Room · Opus — one source of truth',
        role: 'Programme Director',
        url: 'app.skewb.io/operating-layer',
      }),

      { type: 'clear-effect', duration: 0, params: { id: 'layer-assemble' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'connected' } } as ScenarioStep,
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // SCENE 3 — CAPITAL DELIVERY · govern the investment  (1:00–1:30)
    // Portfolio RAG. AI ranks the risks; one scheme (KTM-2209) is held in
    // exception. Our scheme MRP-4471 passes its stage gate, cleared to plan.
    // ═══════════════════════════════════════════════════════════════════════
    act('Scene 3 · Capital Delivery', [
      { type: 'color-grade', duration: 0, params: { tint: '#1e40af', intensity: 0.06, vignette: 0.16 } } as ScenarioStep,

      beat(PACING.ESTABLISH, [
        establishingWide(HUB, { bearing: -12, pitch: 44, zoom: 13.5 }),
        { type: 'multi-site-rag', duration: 0, params: {
          id: 'portfolio',
          sites: portfolio(['green', 'red', 'green', 'green', 'amber', 'green', 'green', 'amber']),
          linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
        }},
        { type: 'model-status', duration: 0, params: { coordinates: gp(-2, -4), infraId: 'control-centre', color: '#1e40af', emissive: 0.6 } },
        hudEvent('programme', 'CAPITAL DELIVERY — REGULATORY PROGRAMME', '625 schemes, £42M programme. RAG-rated, with CDM, G17 and stage-gate evidence captured as part of the work.', 'info'),
        hudRegion([
          { name: 'On Track', status: 'green', properties: 583 },
          { name: 'At Risk', status: 'amber', properties: 34 },
          { name: 'In Exception', status: 'red', properties: 8 },
        ]),
      ]),

      // AI ranks the risk — KTM-2209 held at the gate
      beat(PACING.PUSH_IN, [
        dronePushIn(EXCEPTION, { bearing: 25, zoom: 15.5 }),
        { type: 'model-status', duration: 0, params: { coordinates: gp(14, -16), infraId: 'depot-east', color: '#ef4444', emissive: 0.8 } },
        panel('cap-risk', 'AI Risk Review — KTM-2209', 'data', EXCEPTION, [
          { label: 'Stage Gate', value: 'Pre-Construction HELD', status: 'critical' },
          { label: 'Forecast Slip', value: '119 days', status: 'critical' },
          { label: 'AI Risk Rank', value: '#1 of 625', status: 'critical' },
          { label: 'Options', value: 'Still open', status: 'good' },
        ]),
        hudEvent('ai', 'EXCEPTION HELD AT GATE — KTM-2209', 'AI ranks the risks that matter. Kingsway trunk main held at Pre-Construction — intervene while options exist.', 'warning'),
      ]),
      removePanel('cap-risk'),

      // Our scheme MRP-4471 passes its gate — cleared to plan
      beat(PACING.BEAT, [
        dronePushIn(SCHEME, { bearing: -10, zoom: 15.5 }),
        { type: 'model-status', duration: 0, params: { coordinates: gp(11, 22), infraId: 'sub-south', color: '#10b981', emissive: 0.6 } },
        panel('cap-scheme', 'MRP-4471 — Elm Avenue Renewal', 'data', SCHEME, [
          { label: 'Scheme Value', value: '£820K', status: 'good' },
          { label: 'Stage Gate', value: 'Pre-Construction PASSED', status: 'good' },
          { label: 'CDM / G17', value: 'Evidence captured', status: 'good' },
          { label: 'Decision', value: 'Approved to plan', status: 'good' },
        ]),
        hudEvent('gate', 'STAGE GATE CLEARED — MRP-4471', 'Pre-Construction readiness passed, assurance captured. Scheme approved to mobilise — handed to planning.', 'success'),
      ]),
      removePanel('cap-scheme'),

      productScreen('Capital Delivery — Regulatory Programme Portfolio', {
        component: 'capital-delivery',
        device: 'desktop',
        subtitle: 'RAG portfolio · AI risk ranking · stage-gate assurance',
        role: 'Investment Manager',
        url: 'app.skewb.io/capital-delivery/portfolio',
      }),

      { type: 'clear-effect', duration: 0, params: { id: 'portfolio' } } as ScenarioStep,
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // SCENE 4 — OPTIMAL RESOURCE PLANNING · the efficiency engine  (1:30–2:05)
    // Inbound demand pours into ORP. The three Elm Avenue jobs are bundled, then
    // the package is matched to the best-fit crew with the four dials turning.
    // ═══════════════════════════════════════════════════════════════════════
    act('Scene 4 · Optimal Resource Planning', [
      { type: 'color-grade', duration: 0, params: { tint: '#2dd4bf', intensity: 0.05, vignette: 0.12 } } as ScenarioStep,
      { type: 'model-status', duration: 0, params: { coordinates: gp(-2, -4), infraId: 'control-centre', color: '#2dd4bf', emissive: 0.7 } } as ScenarioStep,

      // Inbound demand — connection, maintenance, mains scheme — into ORP
      beat(PACING.PUSH_IN, [
        { type: 'camera', duration: PACING.PUSH_IN, params: { center: gp(-2, -4), infraId: 'control-centre', zoom: 14.5, pitch: 52, bearing: -8 } },
        convergence('demand-in',
          [at(20, 16), at(2, 26), infra('sub-south', gp(11, 22))],  // CR-8841 · MNT-2207 · MRP-4471
          HUB, PACING.PUSH_IN, { color: '#2dd4bf', speed: 0.5 }),
        hudEvent('orp', 'DEMAND IN', 'Connection CR-8841, maintenance MNT-2207 and scheme MRP-4471 all flow into ORP — same street, same 180m.', 'info'),
      ]),

      // Bundle — 3 jobs become one work package, one visit instead of three
      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(-2, -4), infraId: 'control-centre', zoom: 15, pitch: 50, bearing: 0 } },
        hudEvent('orp', 'BUNDLED → WP-4471', 'ORP spots the overlap and merges the three jobs into one work package: one mobilisation, one window.', 'success'),
        { type: 'hud-metric', duration: 0, params: { id: 'digs', kind: 'counter', label: 'Site Visits', value: 1, unit: '', severity: 'success' } },
      ]),
      { type: 'clear-effect', duration: 0, params: { id: 'demand-in' } } as ScenarioStep,

      // Candidate crews light up
      { type: 'model-status', duration: 0, params: { coordinates: gp(-20, 10), infraId: 'depot-west', color: '#22c55e', emissive: 0.5 } } as ScenarioStep,
      { type: 'model-status', duration: 0, params: { coordinates: gp(6, -18), infraId: 'depot-south', color: '#f59e0b', emissive: 0.4 } } as ScenarioStep,
      { type: 'model-status', duration: 0, params: { coordinates: gp(-24, -10), infraId: 'pump-beta', color: '#f59e0b', emissive: 0.4 } } as ScenarioStep,

      // THE MATCH — ride-hailing for crews. Radar sweep locks onto NV-12.
      beat(PACING.ORBIT, [
        orbit180(HUB, { startBearing: 0, arc: 120, zoom: 14.5, pitch: 50, duration: PACING.ORBIT }),
        matchBeam('match-beam', HUB, [
          { at: infra('depot-west', gp(-20, 10)), best: true },
          { at: infra('depot-south', gp(6, -18)) },
          { at: infra('pump-beta', gp(-24, -10)) },
        ], PACING.ORBIT, { color: '#2dd4bf', winColor: '#22c55e', radius: 380, lockAt: 0.6 }),
        hudEvent('orp', 'MATCHING CREWS', 'ORP scores every available gang on capacity, availability, past performance and cost — in real time.', 'info'),
        hudTeam([
          { name: 'Gang NV-12 · West', status: 'on-track', destination: 'Fit 88% · avail now · −£2.1K' },
          { name: 'Gang NV-08 · South', status: 'monitoring', destination: 'Avail now · costs more' },
          { name: 'Gang NV-33 · North', status: 'monitoring', destination: 'Booked to Thursday' },
        ]),
      ]),

      // The winning crew's score — the four dials drawn in-world
      beat(PACING.BEAT, [
        dronePushIn(GANG_NV12, { bearing: 10, zoom: 16.5 }),
        gaugeCluster('orp-dials', GANG_NV12, [
          { value: 0.88, color: '#0ea5e9' },   // capacity
          { value: 0.95, color: '#22c55e' },   // availability
          { value: 0.92, color: '#a855f7' },   // performance
          { value: 0.86, color: '#f59e0b' },   // cost fit
        ], 2500, { spacing: 26, gaugeRadius: 9 }),
        panel('orp-match', 'ORP — Best-Fit Match', 'data', GANG_NV12, [
          { label: 'Selected', value: 'Gang NV-12 (Morrison)', status: 'good' },
          { label: 'Capacity Fit', value: '88%', status: 'good' },
          { label: 'On Site', value: 'In 22 min', status: 'good' },
          { label: 'Cost vs next-best', value: '−£2.1K', status: 'good' },
        ]),
        hudEvent('orp', 'CREW MATCHED — NV-12', 'Best fit on all four dials. NV-08 costs more; NV-33 is booked to Thursday. WP-4471 assigned to NV-12.', 'success'),
      ]),
      { type: 'clear-effect', duration: 0, params: { id: 'match-beam' } } as ScenarioStep,

      // Dispatch to the street
      spawnVehicle('orp-van', 'simpoly-pickup', GANG_NV12, ELM, 'NV-12', 8000),
      beat(PACING.FOLLOW, [
        { type: 'camera-follow', duration: PACING.FOLLOW, params: { vehicleId: 'orp-van', pitch: 78, zoom: 18.5 } },
        hudEvent('orp', 'CREW DISPATCHED', 'NV-12 en route to Elm Avenue with the full bundled scope. One crew, one visit — instead of three.', 'info'),
      ]),
      removePanel('orp-match'),
      { type: 'clear-effect', duration: 0, params: { id: 'orp-dials' } } as ScenarioStep,

      productScreen('Optimal Resource Planning — Allocation Board', {
        component: 'orp-allocation',
        device: 'desktop',
        subtitle: 'Ride-hailing for crews — 3 jobs bundled, best-fit gang matched',
        role: 'Resource Planner',
        url: 'app.skewb.io/orp/allocation',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // SCENE 5 — THE CONTROL ROOM · deliver in real time  (2:05–2:40)
    // Scheme cards (About to Start / In Progress / Completed). WP-4471 goes
    // live. A real jeopardy — scope exceeds the permit window — caught and
    // cleared before it becomes an overrun and a penalty.
    // ═══════════════════════════════════════════════════════════════════════
    act('Scene 5 · The Control Room', [
      { type: 'color-grade', duration: 0, params: { tint: '#0ea5e9', intensity: 0.05, vignette: 0.14 } } as ScenarioStep,

      beat(PACING.PUSH_IN, [
        craneReveal(HUB, { zoom: 14, bearing: -10 }),
        { type: 'model-status', duration: 0, params: { coordinates: gp(-2, -4), infraId: 'control-centre', color: '#0ea5e9', emissive: 0.6 } },
        { type: 'model-status', duration: 0, params: { coordinates: gp(9, 23), infraId: 'mains-renewal-st', color: '#0ea5e9', emissive: 0.6 } },
        hudEvent('control', 'WP-4471 — IN PROGRESS', 'NV-12 on site, scope confirmed. Scheme card moved About-to-Start → In Progress on the programme wall.', 'info'),
        hudRegion([
          { name: 'About to Start', status: 'amber', properties: 14 },
          { name: 'In Progress', status: 'green', properties: 28 },
          { name: 'Completed today', status: 'green', properties: 113 },
        ]),
      ]),

      // Jeopardy — scope (6d) exceeds the permit window (5d)
      { type: 'model-status', duration: 0, params: { coordinates: gp(9, 23), infraId: 'mains-renewal-st', color: '#f59e0b', emissive: 0.7 } } as ScenarioStep,
      beat(PACING.PUSH_IN, [
        dronePushIn(ELM, { bearing: 30, zoom: 16.5 }),
        panel('jeopardy', 'Jeopardy — WP-4471', 'data', ELM, [
          { label: 'Scope', value: '6 days', status: 'warning' },
          { label: 'Permit Window', value: '5 days', status: 'critical' },
          { label: 'Risk', value: 'Overrun + £5K FPN', status: 'critical' },
          { label: 'Status', value: 'Escalated', status: 'good' },
        ]),
        hudEvent('jeopardy', 'PERMIT JEOPARDY — WP-4471', 'Bundled scope needs 6 days; permit allows 5. Flagged on day one — before it overruns.', 'warning'),
      ]),

      // Resolved — extension secured before any breach
      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(9, 23), infraId: 'mains-renewal-st', zoom: 16, pitch: 55, bearing: 0 } },
        { type: 'model-status', duration: 0, params: { coordinates: gp(9, 23), infraId: 'mains-renewal-st', color: '#10b981', emissive: 0.5 } },
        updatePanel('jeopardy', [
          { label: 'Permit', value: 'Extended +1 day', status: 'good' },
          { label: 'New Window', value: '6 days', status: 'good' },
          { label: 'Overrun Risk', value: 'Cleared', status: 'good' },
          { label: 'FPN', value: 'Avoided (£5K)', status: 'good' },
        ]),
        hudEvent('control', 'EXTENSION APPROVED', 'Permit extended to 6 days before works start. No overrun, no penalty — output protected.', 'success'),
      ]),
      removePanel('jeopardy'),

      productScreen('Control Room — Programme Wall', {
        component: 'control-room',
        device: 'desktop',
        subtitle: 'Scheme cards in real time · jeopardy caught before overrun',
        role: 'Control Room Operator',
        url: 'app.skewb.io/control-room/programme',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // SCENE 6 — STREET, COLLABORATION & THE CUSTOMER  (2:40–3:20)
    // Opus on the street. One permit covers all three jobs; the Cost Avoidance
    // Agent fixes an at-risk element pre-issue; three jobs merge into one dig;
    // the main is renewed; reinstated photo-led; the 38 residents are told once.
    // ═══════════════════════════════════════════════════════════════════════
    act('Scene 6 · Street & Customer', [
      { type: 'color-grade', duration: 0, params: { tint: '#f59e0b', intensity: 0.05, vignette: 0.14 } } as ScenarioStep,

      beat(PACING.PUSH_IN, [
        dronePushIn(ELM, { bearing: -20, zoom: 16.5 }),
        { type: 'model-status', duration: 0, params: { coordinates: gp(9, 23), infraId: 'mains-renewal-st', color: '#f59e0b', emissive: 0.6 } },
        panel('opus-permit', 'Opus — Permit PMT-2847', 'data', ELM, [
          { label: 'Permit', value: '1 (was 3)', status: 'good' },
          { label: 'Covers', value: 'CR-8841 + MNT-2207 + MRP-4471', status: 'good' },
          { label: 'Road', value: 'Elm Avenue (B-road)', status: 'good' },
          { label: 'NRSWA', value: 'Issued — in window', status: 'good' },
        ]),
        hudEvent('opus', 'ONE PERMIT — PMT-2847', 'Opus issues a single NRSWA permit covering all three jobs, around the date the customer was promised.', 'info'),
      ]),

      // Cost Avoidance Agent fixes an at-risk element before issue
      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(9, 23), infraId: 'mains-renewal-st', zoom: 17, pitch: 55, bearing: 10 } },
        hudEvent('ai', 'COST AVOIDANCE — PMT-2847', 'Reinstatement spec flagged as a Section 74 charge risk and corrected before the permit was issued.', 'warning'),
      ]),

      // Three jobs converge on one dig — the literal collaboration shot
      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(9, 23), infraId: 'mains-renewal-st', zoom: 17, pitch: 58, bearing: 20 } },
        convergence('dig-merge',
          [at(11, 23), at(7, 23), at(9, 26)],
          infra('mains-renewal-st', DIG_HOLE), PACING.BEAT, { color: '#f59e0b', speed: 0.7 }),
        hudEvent('opus', 'THREE JOBS → ONE DIG', 'Connection, hydrant and renewal merge into one excavation — one permit, one hole, one reinstatement.', 'success'),
      ]),
      { type: 'clear-effect', duration: 0, params: { id: 'dig-merge' } } as ScenarioStep,

      // Open the single dig, renew the main in the same trench
      { type: 'excavation', duration: 4000, params: { coordinates: DIG_HOLE, radius: 14, id: 'elm-dig' } } as ScenarioStep,
      { type: 'pipe-lay', duration: PACING.FOLLOW, params: { id: 'elm-pipe', path: DIG_PIPE, color: '#0ea5e9' } } as ScenarioStep,

      beat(PACING.FOLLOW, [
        orbit180(ELM, { startBearing: 20, arc: 100, zoom: 16, pitch: 60, duration: PACING.FOLLOW }),
        hudEvent('works', '180m MAIN RENEWED', 'New main laid, Oakfield connection teed in, hydrant replaced — all in the one open trench.', 'info'),
        updatePanel('opus-permit', [
          { label: 'Main Renewed', value: '180m complete', status: 'good' },
          { label: 'Connection', value: 'CR-8841 teed in', status: 'good' },
          { label: 'Hydrant', value: 'MNT-2207 replaced', status: 'good' },
          { label: 'Reinstatement', value: 'Photo-led · S81 PASS', status: 'good' },
        ]),
      ]),

      // Customer kept informed — the 38 residents, once
      beat(PACING.ORBIT, [
        orbit180(ELM, { startBearing: 120, arc: 90, zoom: 15.5, pitch: 48, duration: PACING.ORBIT }),
        { type: 'comms-broadcast', duration: PACING.ORBIT, params: {
          infraId: 'mains-renewal-st', coordinates: DIG_HOLE,
          color: '#10b981', radius: 150, targets: ELM_RESIDENTS, id: 'elm-comms',
        }},
        { type: 'highlight-buildings', duration: 0, params: { coordinates: DIG_HOLE, radius: 150 } },
        hudEvent('customer', 'RESIDENTS NOTIFIED', '38 properties and the Oakfield developer informed of a single 6-day window — proactively, not three times.', 'success'),
        notification('REINSTATED & REOPENED', 'Surface reinstated to HAUC spec, checked photo-led against Section 81. Elm Avenue reopened. Zero defects.', 'success', PACING.BEAT),
      ]),

      removePanel('opus-permit'),
      { type: 'clear-effect', duration: 0, params: { id: 'elm-comms' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'elm-dig' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'elm-pipe' } } as ScenarioStep,
      { type: 'model-status', duration: 0, params: { coordinates: gp(9, 23), infraId: 'mains-renewal-st', color: '#10b981', emissive: 0.5 } } as ScenarioStep,

      productScreen('Opus — Permit PMT-2847', {
        component: 'opus-permit',
        device: 'desktop',
        subtitle: 'One permit · Cost Avoidance Agent · photo-led reinstatement',
        role: 'Streetworks Coordinator',
        url: 'app.skewb.io/opus/permit/PMT-2847',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // SCENE 7 — COMBINED VALUE & CLOSE  (3:20–3:45)
    // Pull back out. MRP-4471 closed; the portfolio carries zero exceptions.
    // The before/after of one coordinated job. Majestic pull-back to the network.
    // ═══════════════════════════════════════════════════════════════════════
    act('Scene 7 · Close', [
      { type: 'color-grade', duration: 0, params: { tint: '#10b981', intensity: 0.05, vignette: 0.1 } } as ScenarioStep,

      { type: 'multi-site-rag', duration: 0, params: {
        id: 'portfolio-closed',
        sites: portfolio(['green', 'green', 'green', 'green', 'green', 'green', 'green', 'green']),
        linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
      }} as ScenarioStep,

      dimEnd,

      beat(8000, [
        craneReveal(HUB, { zoom: 13.5, bearing: -18, duration: 8000 }),
        { type: 'model-status', duration: 0, params: { coordinates: gp(-2, -4), infraId: 'control-centre', color: '#2dd4bf', emissive: 0.7 } },
        hudEvent('complete', 'WP-4471 CLOSED', 'Governed, planned, delivered and reinstated on one source of truth — board to gang, the same live picture.', 'success'),
        panel('close', 'WP-4471 — Outcome', 'data', HUB, [
          { label: 'Excavations', value: '1 (was 3)', status: 'good' },
          { label: 'Permits', value: '1 (was 3)', status: 'good' },
          { label: 'Reinstatements', value: '1 (was 3)', status: 'good' },
          { label: 'Penalties', value: '£0', status: 'good' },
        ]),
        hudRegion([
          { name: 'On Track', status: 'green', properties: 591 },
          { name: 'At Risk', status: 'amber', properties: 34 },
          { name: 'In Exception', status: 'red', properties: 0 },
        ]),
      ]),

      removePanel('close'),
      { type: 'clear-effect', duration: 0, params: { id: 'portfolio-closed' } } as ScenarioStep,

      productScreen('WP-4471 — Closed', {
        component: 'combined-close',
        device: 'desktop',
        subtitle: 'One job, four systems — before / after on one source of truth',
        role: 'Programme Director',
        url: 'app.skewb.io/operating-layer/wp-4471',
      }),

      { type: 'color-grade', duration: 0, params: { tint: '#fbbf24', intensity: 0.04, vignette: 0.1 } } as ScenarioStep,

      beat(PACING.PULLBACK, [
        majesticPullback({ bearing: -15 }),
        notification('SKEWB', 'Invest · Plan · Deliver · Street — one operating layer, board to field.', 'success'),
      ]),

      { type: 'color-grade', duration: 0, params: { tint: 'transparent', intensity: 0, vignette: 0 } } as ScenarioStep,
      hold(PACING.HOLD),
    ]),
  ],
);
